import { useEffect, useRef, useState } from 'react'
import { ChevronLeft, ExternalLink, MoreHorizontal, XCircle } from 'lucide-react'
import type { LiveConversation } from '@manaro/contracts'
import { ApiError } from '@/shared/api/client.ts'
import { cn } from '@/shared/lib/cn.ts'
import { useLiveAction } from '../use-support-chat.ts'
import { STATUS_LABELS, StatusAvatar, visitorLabel } from './conversation-list-item.tsx'

function subtitle(item: LiveConversation): string {
  const where = item.businessTitle ? `${item.businessTitle}${item.businessId ? ` · #${item.businessId}` : ''}` : null
  return [STATUS_LABELS[item.status], where].filter(Boolean).join(' · ')
}

function InfoSheet({ item, onClose }: { item: LiveConversation; onClose: () => void }) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const action = useLiveAction(item.id)

  useEffect(() => {
    dialogRef.current?.showModal()
  }, [])

  const close = () => dialogRef.current?.close()
  const rows: Array<[string, string | null]> = [
    ['Почта', item.visitorEmail],
    ['Салон', item.businessTitle ? `${item.businessTitle}${item.businessId ? ` · #${item.businessId}` : ''}` : null],
    ['Язык', item.locale.toUpperCase()],
    ['Ведёт', item.operatorName],
  ]

  return (
    <dialog
      ref={dialogRef}
      onClose={onClose}
      onClick={(event) => event.target === dialogRef.current && close()}
      aria-labelledby="chat-info-title"
      className={cn(
        'border-0 bg-surface p-0 text-ink shadow-panel backdrop:bg-[rgb(27_27_24/0.35)]',
        'mx-0 mt-auto mb-0 w-full max-w-none rounded-t-[22px]',
        'md:m-auto md:w-[min(440px,calc(100vw-32px))] md:rounded-panel',
      )}
    >
      <div className="flex flex-col gap-3 px-5 pt-2.5 pb-[calc(16px+env(safe-area-inset-bottom))] md:pt-5">
        <div className="mx-auto h-1 w-10 rounded-full bg-line-strong md:hidden" aria-hidden />
        <div className="flex items-center gap-3">
          <StatusAvatar item={item} />
          <div className="min-w-0">
            <h2 id="chat-info-title" className="truncate text-[17px] font-semibold">
              {visitorLabel(item)}
            </h2>
            <p className="text-[13px] text-ink-muted">{STATUS_LABELS[item.status]}</p>
          </div>
        </div>

        <dl className="flex flex-col divide-y divide-line-soft rounded-card border border-line-soft">
          {rows
            .filter(([, value]) => value)
            .map(([label, value]) => (
              <div key={label} className="flex gap-3 px-3.5 py-2.5 text-[14px]">
                <dt className="w-16 shrink-0 text-ink-muted">{label}</dt>
                <dd className="min-w-0 flex-1 break-words">{value}</dd>
              </div>
            ))}
          {item.pageUrl && (
            <div className="flex gap-3 px-3.5 py-2.5 text-[14px]">
              <dt className="w-16 shrink-0 text-ink-muted">Писал с</dt>
              <dd className="min-w-0 flex-1">
                <a href={item.pageUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 break-all text-brand-ink underline-offset-2 hover:underline">
                  {item.pageUrl.replace(/^https?:\/\//, '')}
                  <ExternalLink className="size-3.5 shrink-0" aria-hidden />
                </a>
              </dd>
            </div>
          )}
        </dl>

        {action.error && (
          <p role="alert" className="text-[13px] text-warn-ink">
            {action.error instanceof ApiError ? action.error.message : 'Не получилось — попробуйте ещё раз.'}
          </p>
        )}

        {item.status !== 'closed' && (
          <button
            type="button"
            disabled={action.isPending}
            onClick={() => action.mutate('close', { onSuccess: close })}
            className="flex min-h-[48px] items-center justify-center gap-2 rounded-control border border-line-strong text-[15px] text-warn-ink active:bg-warn-tint disabled:opacity-60"
          >
            <XCircle className="size-5" strokeWidth={1.8} aria-hidden />
            Закрыть разговор
          </button>
        )}
      </div>
    </dialog>
  )
}

interface ChatHeaderProps {
  item: LiveConversation
  onBack?: () => void
}

/** Top of the conversation: back to the list on phones, who it is, and details behind «⋯». */
export function ChatHeader({ item, onBack }: ChatHeaderProps) {
  const [infoOpen, setInfoOpen] = useState(false)

  return (
    <header className="flex shrink-0 items-center gap-1 border-b border-line bg-surface px-1.5 pt-[env(safe-area-inset-top)] md:px-3">
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          aria-label="Назад к чатам"
          className="flex size-11 shrink-0 items-center justify-center rounded-full text-brand active:bg-surface-hover"
        >
          <ChevronLeft className="size-7" strokeWidth={2} />
        </button>
      )}
      <button
        type="button"
        onClick={() => setInfoOpen(true)}
        className="flex min-h-[56px] min-w-0 flex-1 items-center gap-2.5 rounded-control px-1.5 text-left"
      >
        <StatusAvatar item={item} size="sm" />
        <span className="min-w-0">
          <span className="block truncate text-[16px] font-semibold tracking-[-0.01em]">{visitorLabel(item)}</span>
          <span className={cn('block truncate text-[12.5px]', item.status === 'waiting_operator' ? 'text-warn-ink' : 'text-ink-muted')}>
            {subtitle(item)}
          </span>
        </span>
      </button>
      <button
        type="button"
        onClick={() => setInfoOpen(true)}
        aria-label="О посетителе и действия"
        className="flex size-11 shrink-0 items-center justify-center rounded-full text-ink-muted active:bg-surface-hover md:hover:bg-surface-hover"
      >
        <MoreHorizontal className="size-5" />
      </button>

      {infoOpen && <InfoSheet item={item} onClose={() => setInfoOpen(false)} />}
    </header>
  )
}
